import { useCallback, useRef } from 'react';
import { PlanName } from '../types';
import { supabase } from '../services/supabaseClient';

declare global {
  interface Window {
    Paddle?: any;
    __paddleInitialized?: boolean;
  }
}

const PADDLE_CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
const IS_SANDBOX = PADDLE_CLIENT_TOKEN?.startsWith('test_');

const PRICE_MAP: Record<string, Record<string, string>> = {
  starter: {
    monthly: import.meta.env.VITE_PADDLE_PRICE_STARTER_MONTHLY ?? 'pri_01kj0w4tr3jerdckxg6rzy9z14',
    annual: import.meta.env.VITE_PADDLE_PRICE_STARTER_ANNUAL ?? 'pri_01kj0w4sj5npe5me4x4wnd1b4n',
  },
  pro: {
    monthly: import.meta.env.VITE_PADDLE_PRICE_PRO_MONTHLY ?? 'pri_01kj0w4s53wtg6rvz2qgjzp2f5',
    annual: import.meta.env.VITE_PADDLE_PRICE_PRO_ANNUAL ?? 'pri_01kj0w4zbpqcet37ge62b9sspd',
  },
  business: {
    monthly: import.meta.env.VITE_PADDLE_PRICE_BUSINESS_MONTHLY ?? 'pri_01kj0w7fj3qrtrr8tens14c4p1',
    annual: import.meta.env.VITE_PADDLE_PRICE_BUSINESS_ANNUAL ?? 'pri_01kj0w7e29f06ed4kmf19y5kqh',
  },
};

function ensurePaddleInitialized(eventCallback?: (event: any) => void): boolean {
  if (!PADDLE_CLIENT_TOKEN) {
    console.error('[Paddle] VITE_PADDLE_CLIENT_TOKEN is missing. Restart Vite after adding it to .env');
    return false;
  }
  if (!window.Paddle) {
    console.error('[Paddle] window.Paddle is undefined. Check that paddle.js script loaded in index.html');
    return false;
  }

  if (!window.__paddleInitialized) {
    if (IS_SANDBOX) {
      window.Paddle.Environment.set('sandbox');
    }
    window.Paddle.Initialize({
      token: PADDLE_CLIENT_TOKEN,
      eventCallback,
    });
    window.__paddleInitialized = true;
    console.log('[Paddle] Initialized (sandbox:', IS_SANDBOX, ')');
  }
  return true;
}

export function usePaddle() {
  const onSuccessRef = useRef<(() => void) | undefined>();

  const handlePaddleEvent = useCallback((event: any) => {
    if (event?.name === 'checkout.completed') {
      console.log('[Paddle] Checkout completed:', event.data);
      setTimeout(() => {
        onSuccessRef.current?.();
      }, 2000);
    }
  }, []);

  const openCheckout = useCallback(
    (options: {
      plan: Exclude<PlanName, 'trial' | 'tester' | 'expired' | 'cancelled'>;
      billing: 'monthly' | 'annual';
      email: string;
      companyId: string;
      onSuccess?: () => void;
    }) => {
      onSuccessRef.current = options.onSuccess;

      if (!ensurePaddleInitialized(handlePaddleEvent)) {
        alert('Payment system is loading. Please try again in a moment.');
        return;
      }

      const priceId = PRICE_MAP[options.plan]?.[options.billing];
      if (!priceId) {
        console.error(`No price found for ${options.plan}/${options.billing}`);
        return;
      }

      window.Paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: { email: options.email },
        customData: { companyId: options.companyId, plan: options.plan },
      });
    },
    [handlePaddleEvent]
  );

  const openPortal = useCallback(async (companyId: string) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Authentication required');
    }

    const res = await supabase.functions.invoke('paddle-management-urls', {
      body: { companyId },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.error) throw res.error;

    const urls = (res.data as { urls?: Record<string, string | null> })?.urls ?? {};
    const preferredUrl =
      urls.customer_portal ||
      urls.update_payment_method ||
      urls.cancel ||
      null;

    if (!preferredUrl) {
      throw new Error('No management URL available for this subscription.');
    }

    window.open(preferredUrl, '_blank', 'noopener,noreferrer');
  }, []);

  return { openCheckout, openPortal, isPaddleReady: !!window.Paddle && !!PADDLE_CLIENT_TOKEN };
}
